/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 */
package studio

/**
 * Effective savings rate contribution: the slice this goal carves out of the
 * user's gross income, split into out-of-pocket dollars and tax-shifted
 * dollars. See §4.3.
 *
 * This is the math that lets the Studio say "RRSP stretches your savings
 * rate further" — the same monthly dollar contribution buys a larger total
 * percentage of saved income inside an RRSP.
 */
object SavingsRateBreakdownCalculator {

    fun compute(
        monthlyContribution: Double,
        vehicle: VehicleType,
        taxContext: UserTaxContext,
    ): SavingsRateBreakdown {
        val gross = taxContext.grossAnnualIncome
        if (gross <= 0) return SavingsRateBreakdown(0.0, 0.0, 0.0)

        val annualContribution = monthlyContribution * 12.0
        val outOfPocket = annualContribution / gross
        val taxShifted = when (vehicle) {
            VehicleType.RRSP, VehicleType.FHSA ->
                (annualContribution * taxContext.marginalRateNow) / gross

            VehicleType.RESP -> {
                // CESG: 20% of contribution up to $500/year.
                val cesg = minOf(annualContribution * 0.20, 500.0)
                cesg / gross
            }

            else -> 0.0
        }
        return SavingsRateBreakdown(
            outOfPocket = outOfPocket,
            taxShifted = taxShifted,
            total = outOfPocket + taxShifted,
        )
    }
}
